<template>
    <v-dialog
        :value="value"
        max-width="560"
        @input="$emit('input', $event)"
    >
        <v-card outlined>
            <v-card-title class="text-h6">
                <v-icon
                    color="primary"
                    class="mr-3"
                >
                    fa-solid fa-right-to-bracket
                </v-icon>
                {{ $t("impersonation.dialogTitle") }}
            </v-card-title>

            <v-card-text class="pt-2">
                <!--
                    Server-side search: `search-input.sync` feeds a debounced watcher that
                    queries the backend. `no-filter` disables Vuetify's client-side filtering.
                    Infinite scroll is driven by the `v-intersect` sentinel in `append-item`.
                -->
                <v-autocomplete
                    v-model="selected"
                    :items="items"
                    :search-input.sync="search"
                    :loading="targetsStatus_Pending"
                    :label="$t('impersonation.searchLabel')"
                    :no-data-text="targetsStatus_Pending ? $t('impersonation.loading') : $t('impersonation.noResults')"
                    item-text="name"
                    item-value="user_id"
                    return-object
                    no-filter
                    clearable
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="fas fa-search"
                >
                    <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ item.name }}
                                    <span
                                        v-if="item.code"
                                        class="grey--text text--darken-1"
                                    >· {{ item.code }}</span>
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ item.head_name || item.email }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-chip
                                    x-small
                                    dark
                                    :color="typeColor(item.type)"
                                >
                                    {{ typeLabel(item.type) }}
                                </v-chip>
                            </v-list-item-action>
                        </v-list-item>
                    </template>

                    <!-- infinite scroll: loads the next page when this row scrolls into view -->
                    <template v-slot:append-item>
                        <div
                            v-if="hasMore"
                            v-intersect="onIntersect"
                            class="pa-2 text-center caption grey--text"
                        >
                            {{ $t("impersonation.loading") }}
                        </div>
                    </template>
                </v-autocomplete>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    color="grey darken-1"
                    :disabled="impersonationStatus_Pending"
                    @click="close"
                >
                    {{ $t("general.cancel") }}
                </v-btn>
                <v-btn
                    color="primary"
                    depressed
                    :disabled="!canSubmit"
                    :loading="impersonationStatus_Pending"
                    @click="confirm"
                >
                    <v-icon
                        size="16"
                        class="mr-2"
                    >
                        fa-solid fa-right-to-bracket
                    </v-icon>
                    {{ $t("impersonation.confirm") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { apiStatus } from "@/api/constants/apiStatus";
import { apiStatusComputed } from "@/api/helpers/computedApiStatus";
import { withAsync } from "@/helpers/withAsync";
import { searchImpersonationTargets } from "@/api/impersonationApi";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 350;

/** Chip colour per entity type (labels live in i18n under `impersonation.types`). */
const TYPE_COLORS = {
    company: "primary",
    food_company: "teal",
    ota: "indigo",
    settlement: "success",
    collector: "warning",
    sed: "purple",
};

/**
 * ImpersonationDialog
 *
 * Admin-only picker to "log in as" another account. Searches impersonatable
 * users by email/head_email, by settlement ΑΜ ("company_id-serial") or by entity
 * id — via a paginated backend endpoint, with debounced querying and infinite
 * scroll. Each result shows its `code` (the ΑΜ for settlements, the id otherwise).
 * On confirm it delegates to the `auth/startImpersonation` store action (which
 * swaps identity + reloads).
 *
 * Usage: `<impersonation-dialog v-model="show" />`
 */
export default {
    name: "ImpersonationDialog",

    props: {
        /** v-model: whether the dialog is open. */
        value: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            targetsStatus: apiStatus.Idle,
            impersonationStatus: apiStatus.Idle,

            search: "",       // bound to the autocomplete input
            term: "",         // the active (committed) search term, used for load-more
            items: [],        // accumulated results across pages
            selected: null,   // the chosen target (full object)
            page: 1,
            lastPage: 1,
            debounceTimer: null,
            requestId: 0, // monotonic id so only the latest request applies its result
        };
    },

    computed: {
        ...apiStatusComputed(["targetsStatus", "impersonationStatus"]),
        hasMore() {
            return this.page < this.lastPage;
        },
        canSubmit() {
            return !!this.selected && !this.impersonationStatus_Pending;
        },
    },

    methods: {
        reset() {
            clearTimeout(this.debounceTimer);
            this.requestId += 1; // discard any in-flight response from a previous open
            this.search = "";
            this.term = "";
            this.items = [];
            this.selected = null;
            this.page = 1;
            this.lastPage = 1;
            this.targetsStatus = apiStatus.Idle;
            this.impersonationStatus = apiStatus.Idle;
        },

        onSearchInput(val) {
            if (this.selected) {
                // Vuetify sets the input to the selected item's name — ignore that echo
                if (val === this.selected.name) return;
                // the admin edited the text away from the selection → invalidate it
                this.selected = null;
            }

            clearTimeout(this.debounceTimer);
            const term = (val || "").trim();

            if (term.length < MIN_CHARS) {
                this.requestId += 1; // discard any in-flight response
                this.items = [];
                this.term = "";
                this.page = 1;
                this.lastPage = 1;
                this.targetsStatus = apiStatus.Idle;
                return;
            }

            this.debounceTimer = setTimeout(() => this.runSearch(term, { reset: true }), DEBOUNCE_MS);
        },

        async runSearch(term, { reset }) {
            const page = reset ? 1 : this.page + 1;
            const requestId = ++this.requestId; // claim latest; older responses are dropped
            this.targetsStatus = apiStatus.Pending;

            const { response, error } = await withAsync(searchImpersonationTargets, term, page);

            if (requestId !== this.requestId) return; // superseded by a newer search

            if (error) {
                // surfaced by the global api interceptor; page left unchanged → load-more is retryable
                this.targetsStatus = apiStatus.Error;
                return;
            }

            const { data } = response;
            this.term = term;
            this.page = page;
            this.lastPage = data.meta ? data.meta.last_page : 1;
            this.items = reset ? data.data : [...this.items, ...data.data];
            this.targetsStatus = apiStatus.Success;
        },

        onIntersect(entries, observer, isIntersecting) {
            if (isIntersecting && this.hasMore && !this.targetsStatus_Pending) {
                this.runSearch(this.term, { reset: false });
            }
        },

        async confirm() {
            if (!this.canSubmit) return;
            this.impersonationStatus = apiStatus.Pending;
            // on success this hard-reloads into the impersonated session;
            // the line below is only meaningfully reached if the request failed.
            await this.$store.dispatch("auth/startImpersonation", this.selected.user_id);
            this.impersonationStatus = apiStatus.Idle;
        },

        close() {
            this.$emit("input", false);
        },

        typeLabel(type) {
            return this.$t(`impersonation.types.${type}`);
        },

        typeColor(type) {
            return TYPE_COLORS[type] || "grey";
        },
    },

    watch: {
        // reset everything each time the dialog opens
        value(open) {
            if (open) this.reset();
        },
        search(val) {
            this.onSearchInput(val);
        },
    },
};
</script>
